import { useTranslation } from 'react-i18next'
import type { ReactNode } from 'react'
import './componentes.css'

/**
 * Os cinco estados obrigatórios de UI-11, num componente só.
 *
 * Existir aqui é o que torna a diretriz cumprível: se cada tela tivesse de
 * inventar seu "carregando" e seu "vazio", metade nasceria sem os cinco. E
 * `vazio` é informação, não erro — dizer "nada encontrado" com cara de falha
 * ensina o usuário a desconfiar de um resultado correto.
 */
export type TipoEstado = 'carregando' | 'vazio' | 'erro' | 'semPermissao'

const ICONE: Record<TipoEstado, string> = {
  carregando: '◌',
  vazio: '—',
  erro: '!',
  semPermissao: '⊘',
}

export function Estado({
  tipo,
  titulo,
  descricao,
  acao,
}: {
  tipo: TipoEstado
  titulo?: string
  descricao?: string
  acao?: ReactNode
}) {
  const { t } = useTranslation()

  const padrao = {
    carregando: [t('comum.carregando'), ''],
    vazio: [t('comum.vazio'), t('comum.vazioDescricao')],
    erro: [t('comum.erro'), t('comum.erroDescricao')],
    semPermissao: [t('comum.semPermissao'), t('comum.semPermissaoDescricao')],
  }[tipo]

  return (
    <div
      className={`estado estado--${tipo}`}
      // Carregando é anúncio educado; erro interrompe. A distinção evita
      // que o leitor de tela corte a leitura do usuário a cada requisição.
      role={tipo === 'erro' ? 'alert' : 'status'}
      aria-live={tipo === 'erro' ? 'assertive' : 'polite'}
    >
      {/* UI-09: além da cor, um símbolo — e ele é decorativo para quem
          usa leitor de tela, porque o texto já diz o mesmo. */}
      <span className="estado__icone" aria-hidden="true">
        {ICONE[tipo]}
      </span>
      <p className="estado__titulo">{titulo ?? padrao[0]}</p>
      {(descricao ?? padrao[1]) && (
        <p className="estado__descricao">{descricao ?? padrao[1]}</p>
      )}
      {acao && <div className="estado__acao">{acao}</div>}
    </div>
  )
}
